import React, { useEffect, useRef, useState } from 'react';
import { transformCoordinate } from '@agissc/ktag-map';
import { useTranslation } from 'react-i18next';
import useAppState from '../../AppStore';
import { useTracking } from '../../utils/tracking/TrackingContext';

const Scalebar = () => {
  const { Tracking } = useTracking();
  const coordinateSystems = [
    { value: 'lv95', text: 'CH1903+ / LV95' },
    { value: 'wgs84', text: 'WGS 84 (lat/lon)' },
  ];

  const { t } = useTranslation('agisviewer');
  const [scale, setScale] = useState('');
  const [width, setWidth] = useState(0);
  const [selectedCoordinateSystem, setSelectedCoordinateSystem] = useState(
    coordinateSystems[0].value,
  );

  const scalebarRef = useRef<HTMLDivElement>(null);
  const location = useAppState((state) => state.location);
  const mouseLocation = useAppState((state) => state.mouseLocation);

  const handlCoordinateSystemChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    Tracking.Scalebar.changeCoordinateSystem(event.target.value);
    setSelectedCoordinateSystem(event.target.value);
  };

  const calculateScale = (value: number) => {
    if (value < 0.1) {
      return Math.ceil(value * 100) / 100;
    }
    if (value < 1) {
      return Math.ceil(value * 10) / 10;
    }
    if (value < 10) {
      return Math.ceil(value);
    }
    if (value < 100) {
      return Math.ceil(value / 10) * 10;
    }
    if (value < 1000) {
      return Math.ceil(value / 100) * 100;
    }
    return Math.ceil(value / 1000) * 1000;
  };

  const formatNumber = (number: number) => {
    if (Number.isInteger(number)) {
      return number.toString();
    }
    return number.toFixed(1);
  };

  const formatCoordinates = () => {
    if (selectedCoordinateSystem === coordinateSystems[0].value)
      return `${Math.round(mouseLocation[0])} ${Math.round(mouseLocation[1])}`;
    const wgs84Coordinate = transformCoordinate(
      mouseLocation,
      'EPSG:2056',
      'EPSG:4326',
    ) as Number[];
    return `${wgs84Coordinate[1].toFixed(4)} ${wgs84Coordinate[0].toFixed(4)}`;
  };

  useEffect(() => {
    const mapContainer = scalebarRef.current?.closest(
      '[data-component="MapContainer"]',
    ) as HTMLElement;

    const mapWidthInPixel = mapContainer?.offsetWidth;
    if (!mapWidthInPixel || !location.extent) return;
    const mapWidthInMeters = Number(location.extent.xMax) - Number(location.extent.xMin);

    const metersPerPixel = mapWidthInMeters / mapWidthInPixel;
    const scaleLengthInPixel = 100;
    const realWorldDistanceInMeters = scaleLengthInPixel * metersPerPixel;
    const roundedScale = calculateScale(realWorldDistanceInMeters);

    setWidth((scaleLengthInPixel / realWorldDistanceInMeters) * roundedScale);

    if (realWorldDistanceInMeters < 1000) {
      setScale(`${formatNumber(roundedScale)}m`);
    } else {
      setScale(`${formatNumber(roundedScale / 1000)}km`);
    }
  }, [location, scalebarRef]);

  return (
    <div
      ref={scalebarRef}
      className="location-display"
      title={t('change_coordinate_system')}
    >
      <select
        className="location-display__select"
        value={selectedCoordinateSystem}
        onChange={handlCoordinateSystemChange}
      >
        {coordinateSystems.map((option) => (
          <option key={option.value} value={option.value}>
            {option.text}
          </option>
        ))}
      </select>
      <div className="location-display__coords">{formatCoordinates()}</div>
      <div className="location-display__scalebar_container">
        <div
          style={{ width: `${width}px` }}
          className="location-display__scalebar_container__scalebar"
        />
        <div>{scale}</div>
      </div>
    </div>
  );
};

export default Scalebar;
